/**
 * The search app's control strip: status scope, match mode, and — the point of
 * the whole thing — the DEFAULTS the backend applied, shown as chips the user
 * can take off.
 *
 * items 2, 5, 6.
 *
 * WHY CHIPS. Every one of these knobs has a default that is right most of the
 * time and badly wrong some of the time. "Current documents only" is the right
 * default for someone browsing today's requirements and exactly the wrong one
 * for someone chasing a rescinded 1998 order. A silent default in that second
 * case reads as "the archive doesn't have it" — the user has no way to know a
 * filter was ever applied. So: the backend reports what it defaulted
 * (`*_defaulted` in `search_meta`), and every defaulted value appears here as a
 * chip with an ✕. Nothing is hidden and nothing needs a settings trip.
 *
 * WHY THE DEFAULTS DIFFER BY INTENT. Measured, not guessed. Applying `Current`
 * to termed searches collapsed the gold suite from P@1 0.893 to 0.311 — roughly
 * two thirds of the corpus is superseded, and a document search that silently
 * drops it is broken. So a search WITH a term defaults to `All` and a search
 * WITHOUT one (i.e. an empty-box landing on /search) defaults to `Current`,
 * which is a browse surface where "today's requirements" is genuinely right.
 * The rationale is recorded in search-registry.json's `status_control` comment.
 *
 * SORT is deliberately NOT a chip. Its default (newest-first when there is no
 * term, relevance when there is) is surfaced where sort actually lives in this
 * design — the caret on the sorted column in the results table, which reads the
 * same `search_meta.sort_on`. A second, removable sort control next to it would
 * be two widgets fighting over one piece of state.
 *
 * All state is in the URL (`?status_group=`/`?operator=`), so a search is
 * shareable, bookmarkable, and back/forward works. The route (theme/Search)
 * owns the URL; this component only says what changed.
 */
import React from 'react';
import PropTypes from 'prop-types';

import {
  STATUS_GROUPS,
  SEARCH_OPERATORS,
  SITE_CHOICES,
} from '../../config/searchRegistry.generated';

/** Human wording for a status group id, used in the default chip. */
const STATUS_CHIP_LABEL = {
  Current: 'Current documents only',
  Archive: 'Archived documents only',
  All: 'All documents',
};

/** Human wording for the match operator. */
const OPERATOR_LABEL = {
  OR: 'Any word',
  AND: 'All words',
};

const OPERATOR_HINT = {
  OR: 'Matches documents containing any of your words (best recall).',
  AND: 'Matches only documents containing every word you typed.',
};

const Segmented = ({ legend, hint, options, value, onChange }) => (
  <div className="search-controls__group" role="group" aria-label={legend}>
    <span className="search-controls__legend">{legend}</span>
    <div className="search-controls__segments">
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            className={`search-controls__segment${active ? ' is-active' : ''}`}
            aria-pressed={active}
            title={opt.hint}
            onClick={() => !active && onChange(opt.id)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
    {hint ? <span className="search-controls__hint">{hint}</span> : null}
  </div>
);

const Chip = ({ label, title, onRemove }) => (
  <span className="search-controls__chip" title={title}>
    <span className="search-controls__chip-label">{label}</span>
    <button
      type="button"
      className="search-controls__chip-remove"
      aria-label={`Remove filter: ${label}`}
      onClick={onRemove}
    >
      ×
    </button>
  </span>
);

const SearchControls = ({ meta, onChange, onClearScope }) => {
  // Nothing to say until the seam has answered once. (The addon reducer keeps
  // the previous meta alive across refetches, so this only hides the control
  // strip on a genuinely first, never-loaded render — not on every keystroke.)
  // The pending placeholder reserves the strip's height so the results are
  // not shoved down when it fills in -- that insertion was the largest
  // layout shift Lighthouse attributed to /search (CLS 0.23).
  if (!meta)
    return (
      <div
        className="search-controls search-controls--pending"
        aria-hidden="true"
      />
    );

  const hasTerm = !!meta.term;
  const statusValue = meta.status_group || 'All';
  const operatorValue = meta.operator || 'OR';

  // The backend reports its own group list, so a group added to the registry
  // shows up here with no frontend change. Fall back to the generated copy if
  // an older backend answers without it.
  const groups = meta.status_groups?.length
    ? meta.status_groups
    : STATUS_GROUPS;
  const operators = meta.operators?.length ? meta.operators : SEARCH_OPERATORS;

  const scopeSites = Array.isArray(meta.scope)
    ? meta.scope.filter(Boolean)
    : [];
  const scopeLabel =
    SITE_CHOICES.find((s) => s.value === scopeSites[0])?.label ||
    'this archive';

  const chips = [];
  // A defaulted status is only worth showing when it actually excludes
  // something: defaulting to "All" hides nothing, so it is not a chip.
  if (meta.status_defaulted && statusValue !== 'All') {
    chips.push({
      key: 'status',
      label: STATUS_CHIP_LABEL[statusValue] || statusValue,
      title:
        'Applied by default. Remove it to search current and archived documents together.',
      onRemove: () => onChange({ status_group: 'All' }),
    });
  }
  // Scope is defaulted to the archive the user is standing in. Removing it
  // widens the search to every indexed archive — the cross-site case that is
  // otherwise buried in the left-rail Archive facet.
  if (meta.scope_defaulted && scopeSites.length === 1 && onClearScope) {
    chips.push({
      key: 'scope',
      label: `${scopeLabel} only`,
      title: 'Applied by default. Remove it to search all archives.',
      onRemove: onClearScope,
    });
  }

  // Defaults row FIRST: it sits directly under FilterList's "Filters:" row
  // (see LeftColumnFacets.jsx render order) so the two chip lines read as one
  // labelled system, with the SHOW/MATCH controls below them.
  return (
    <div className="search-controls">
      {chips.length > 0 && (
        <div className="search-controls__defaults">
          <span className="search-controls__defaults-label">
            Applied by default:
          </span>
          {chips.map((c) => (
            <Chip
              key={c.key}
              label={c.label}
              title={c.title}
              onRemove={c.onRemove}
            />
          ))}
        </div>
      )}
      <div className="search-controls__row">
        <Segmented
          legend="Show"
          options={groups.map((g) => ({
            id: g.id,
            label: g.label,
            hint:
              g.id === 'Current'
                ? 'Documents that are still in force.'
                : g.id === 'Archive'
                  ? 'Superseded, rescinded and canceled documents.'
                  : 'Everything in the archive.',
          }))}
          value={statusValue}
          onChange={(id) => onChange({ status_group: id })}
        />
        {/* The operator only means something once there are words to combine. */}
        {hasTerm && operators.length > 1 && (
          <Segmented
            legend="Match"
            hint={OPERATOR_HINT[operatorValue]}
            options={operators.map((op) => ({
              id: op,
              label: OPERATOR_LABEL[op] || op,
              hint: OPERATOR_HINT[op],
            }))}
            value={operatorValue}
            onChange={(id) => onChange({ operator: id })}
          />
        )}
      </div>
    </div>
  );
};

SearchControls.propTypes = {
  /** `search_meta` from the seam — see helpers/useSearchMeta. */
  meta: PropTypes.object,
  /** ({status_group?, operator?}) -> void. Writes the URL; refetch follows. */
  onChange: PropTypes.func.isRequired,
  /** Widen the site scope to every archive. Omit to hide the scope chip. */
  onClearScope: PropTypes.func,
};

export default SearchControls;
